import { FormEvent, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRightLeft, Clock, UserRoundCheck } from "lucide-react";
import { api, json, normalizePage, queryString } from "../../lib/api";
import { useAsync } from "../../lib/useAsync";
import { formatDate, StatusBadge } from "../../components/Badge";
import { Field, Select, Textarea } from "../../components/Form";
import {
  Empty,
  ErrorState,
  Failure,
  Loading,
  Pagination,
  Success,
} from "../../components/State";
import type { Page } from "../../lib/types";

interface ReviewTask {
  id: string;
  application_id: string;
  application_no: string;
  venture_name: string;
  stage_name: string;
  slot_no: number;
  status: string;
  assigned_to?: string;
  assigned_name?: string;
  due_at: string;
  sla_breached_at?: string;
  overdue: boolean;
  lock_version: number;
}
interface Reviewer {
  id: string;
  name: string;
  active_task_count: number;
}

export function AdminReviewTasksPage() {
  const [params, setParams] = useSearchParams();
  const page = Number(params.get("page") || 1);
  const status = params.get("status") || "";
  const assignee = params.get("assignee") || "";
  const overdue = params.get("overdue") || "";
  const result = useAsync(
    async () =>
      normalizePage(
        await api<Page<ReviewTask>>(
          `/admin/review-tasks${queryString({ page, pageSize: 20, status, assignee, overdue })}`,
        ),
      ),
    [page, status, assignee, overdue],
  );
  const reviewers = useAsync(
    () => api<{ items: Reviewer[] }>("/admin/reviewers"),
    [],
  );
  const [editing, setEditing] = useState<ReviewTask>();
  const [reviewer, setReviewer] = useState("");
  const [reason, setReason] = useState("manual_assign");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const patch = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.set("page", "1");
    setParams(next);
  };
  const open = (task: ReviewTask) => {
    setEditing(task);
    setReviewer(task.assigned_to || reviewers.data?.items[0]?.id || "");
    setReason(task.assigned_to ? "workload_balance" : "manual_assign");
    setNote("");
    setError("");
  };
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!editing || !reviewer) return;
    setBusy(true);
    setError("");
    try {
      await api(
        `/admin/review-tasks/${editing.id}/${editing.assigned_to ? "transfer" : "assign"}`,
        {
          method: "POST",
          body: json({
            membership_id: reviewer,
            reason_code: reason,
            note,
            lock_version: editing.lock_version,
          }),
        },
      );
      setEditing(undefined);
      setMessage(editing.assigned_to ? "审核任务已转派" : "审核任务已指派");
      await result.retry();
    } catch (value) {
      setError(value instanceof Error ? value.message : "指派失败");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <header className="admin-page-header">
        <div>
          <span>REVIEW QUEUE</span>
          <h1>审核任务</h1>
          <p>审核队列、指派关系与 SLA 状态。</p>
        </div>
      </header>
      {message && <Success>{message}</Success>}
      {error && <Failure>{error}</Failure>}
      <div className="admin-filterbar">
        <Select
          value={status}
          onChange={(e) => patch("status", e.target.value)}
        >
          <option value="">全部状态</option>
          <option value="pending">待分配</option>
          <option value="claimed">处理中</option>
          <option value="completed">已完成</option>
          <option value="cancelled">已取消</option>
        </Select>
        <Select
          value={assignee}
          onChange={(e) => patch("assignee", e.target.value)}
        >
          <option value="">全部审核员</option>
          <option value="me">分配给我</option>
          {reviewers.data?.items.map((item) => (
            <option value={item.id} key={item.id}>
              {item.name} ({item.active_task_count})
            </option>
          ))}
        </Select>
        <Select
          value={overdue}
          onChange={(e) => patch("overdue", e.target.value)}
        >
          <option value="">全部时限</option>
          <option value="true">已逾期</option>
          <option value="false">未逾期</option>
        </Select>
      </div>
      {result.loading ? (
        <Loading />
      ) : result.error ? (
        <ErrorState error={result.error} retry={result.retry} />
      ) : !result.data?.items.length ? (
        <Empty title="暂无审核任务" />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>申请</th>
                <th>阶段</th>
                <th>审核员</th>
                <th>截止时间</th>
                <th>状态</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {(result.data.items as ReviewTask[]).map((item) => (
                <tr key={item.id}>
                  <td>
                    <strong>{item.venture_name}</strong>
                    <small>{item.application_no}</small>
                  </td>
                  <td>
                    {item.stage_name} · 席位 {item.slot_no}
                  </td>
                  <td>{item.assigned_name || "未分配"}</td>
                  <td>
                    <span className={item.overdue ? "danger-text" : ""}>
                      <Clock size={14} />
                      {formatDate(item.due_at)}
                    </span>
                  </td>
                  <td>
                    <StatusBadge status={item.status as never} />
                  </td>
                  <td>
                    <div className="row-actions">
                      <Link to={`/admin/applications/${item.application_id}`}>
                        查看申请
                      </Link>
                      {["pending", "claimed"].includes(item.status) && (
                        <button onClick={() => open(item)}>
                          {item.assigned_to ? (
                            <ArrowRightLeft size={14} />
                          ) : (
                            <UserRoundCheck size={14} />
                          )}{" "}
                          {item.assigned_to ? "转派" : "指派"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pagination
        page={page}
        pageSize={result.data?.page_size || 20}
        total={result.data?.total || 0}
        onChange={(value) => patch("page", String(value))}
      />
      {editing && (
        <div className="modal-layer">
          <button
            className="modal-backdrop"
            aria-label="关闭"
            onClick={() => setEditing(undefined)}
          />
          <form className="modal-card" onSubmit={save}>
            <header>
              <h2>{editing.assigned_to ? "转派审核任务" : "指派审核任务"}</h2>
              <button type="button" onClick={() => setEditing(undefined)}>
                ×
              </button>
            </header>
            <Field label="审核员" required>
              <Select
                required
                value={reviewer}
                onChange={(e) => setReviewer(e.target.value)}
              >
                <option value="">请选择</option>
                {reviewers.data?.items.map((item) => (
                  <option value={item.id} key={item.id}>
                    {item.name} · {item.active_task_count} 项处理中
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="原因" required>
              <Select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              >
                <option value="manual_assign">人工分配</option>
                <option value="workload_balance">工作量平衡</option>
                <option value="reviewer_unavailable">审核员不可用</option>
                <option value="sla_escalation">SLA 升级</option>
                <option value="admin_error">管理纠错</option>
                <option value="other">其他</option>
              </Select>
            </Field>
            <Field label="说明">
              <Textarea
                rows={4}
                maxLength={4000}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </Field>
            <button className="primary-button" disabled={busy}>
              {busy ? "提交中..." : "确认"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
